import { useMemo, useState, type ChangeEvent } from 'react';
import type {
  CreateSiteRequest,
  FlagState,
  ImportCommitDevice,
  ImportCommitRequest,
  ImportCommitSite,
  ImportCommitVendor,
  ImportResult,
  VesselType,
} from '@mvcp/contracts';
import { commitImport, getDevices, getSites, getVendors } from '../api';
import { ConfirmModal } from './ConfirmModal';
import { normalizeDeviceCategory } from '../lib/device-category';
import {
  applyMapping,
  classifyImport,
  columnLabels,
  columnSamples,
  elide,
  mappedColumnsByField,
  mappingTarget,
  readImportFile,
  suggestMapping,
  validateImportRow,
  HEADER_ALIAS_HELP,
  IGNORE_LABEL,
  IMPORT_FIELDS,
  SITE_ATTRIBUTE_LABELS,
  SITE_ATTRIBUTES_BY_TYPE,
  type ClassifiedDevice,
  type ClassifiedSite,
  type ClassifiedVendor,
  type ImportClassification,
  type ImportColumnMapping,
  type ImportField,
  type ParsedImportFile,
  type RowCheck,
  type RowVocabulary,
  type SiteAttribute,
} from '../lib/import-parse';

/**
 * The attributes whose values the file contradicted for this Site, in
 * {@link SITE_ATTRIBUTES_BY_TYPE}'s stable order (DEV-4444).
 *
 * One reader for both consumers — the Import-blocking gate and the review step's
 * controls — so the set the Manager is asked to resolve can never differ from the
 * set that blocks them.
 */
function siteConflicts(s: ClassifiedSite): SiteAttribute[] {
  if (!s.conflicts) return [];
  return SITE_ATTRIBUTES_BY_TYPE[s.type].filter((a) => s.conflicts![a] !== undefined);
}

/** The values the Manager may choose between for one contradicted attribute: winner first. */
function siteConflictOptions(s: ClassifiedSite, attribute: SiteAttribute): string[] {
  const record = s.conflicts?.[attribute];
  return record ? [record.won, ...record.values] : [];
}

/** One contradicted attribute, resolved: the value that commits and the ones that do not. */
interface SiteConflictAttribute {
  attribute: SiteAttribute;
  label: string;
  /**
   * What commits — the Manager's pick. On a MATCHED Site nothing commits at all, so
   * this is the file's first value and is informational only.
   *
   * `undefined` means no pick exists, which `onConfirmMapping`'s seeding makes
   * unreachable. It is modelled rather than defaulted away so that a regression in the
   * seeding SHOWS — see {@link siteConflictSummary}.
   */
  saved: string | undefined;
  /** The OFFERED values that lose. Never contains `saved`. */
  discarded: string[];
  /** Values the cap held back. Offered to nobody, so neither list above names them. */
  more: number;
}

/** A Site the file contradicted itself about, and what the import will do with it. */
interface SiteConflictSummary {
  key: string;
  name: string;
  /** Carried because identity is `lower(name)::type` — a name alone can name two Sites. */
  type: ClassifiedSite['type'];
  /** A matched Site writes NOTHING from the file — `saved` is then a label, not an outcome. */
  matched: boolean;
  attributes: SiteConflictAttribute[];
}

/**
 * Every conflicted Site and its resolution, derived once from the classification and
 * the Manager's picks (DEV-4444 QA round 2).
 *
 * THE single derivation behind three surfaces: the confirmation dialog the Manager
 * acknowledges, the done screen that repeats it afterwards, and the emptiness test
 * that decides whether a dialog appears at all. Deriving any of them separately is
 * how the acknowledged import and the reported one drift apart — which is the whole
 * complaint this round of QA raised about the first fix.
 */
function siteConflictSummary(
  sites: ClassifiedSite[],
  sitePick: Record<string, string>,
): SiteConflictSummary[] {
  const summaries: SiteConflictSummary[] = [];
  for (const s of sites) {
    const conflicts = siteConflicts(s);
    if (conflicts.length === 0) continue;
    const matched = s.match === 'matched';
    summaries.push({
      key: s.key,
      name: s.name,
      type: s.type,
      matched,
      attributes: conflicts.map((attribute) => {
        const record = s.conflicts![attribute]!;
        // A matched Site is offered no control and writes nothing, so `won` is the only
        // value it can name — a label, not an outcome.
        //
        // An unmatched one reads `sitePick` with NO `?? record.won` fallback, and that
        // absence is load-bearing. A fallback here (and the twin the picker used to
        // have) made the seeding invisible: every assertion about the default read the
        // rendered control, which produced `won` whether or not `sitePick` held
        // anything, so deleting the seeding loop left the whole suite green while the
        // product silently returned to first-row-wins — the exact defect DEV-4444
        // exists to remove. With the fallback gone, `sitePick` is the single source of
        // truth for the picker, this summary and `siteCreate` alike, a missing seed is
        // VISIBLE, and the refusal in `siteCreate` agrees with what is on screen.
        const saved = matched ? record.won : sitePick[`${s.key}::${attribute}`];
        return {
          attribute,
          label: SITE_ATTRIBUTE_LABELS[attribute],
          saved,
          discarded: siteConflictOptions(s, attribute).filter((v) => v !== saved),
          more: record.more,
        };
      }),
    });
  }
  return summaries;
}

/**
 * The conflicted Sites, written for acknowledgement (`when: 'before'` — the dialog)
 * or as the record of what happened (`when: 'after'` — the done screen).
 *
 * ONE component for both so the two surfaces cannot describe one resolution two ways.
 * The point of this QA round is that the Manager is told the same thing twice, not
 * two different things once each.
 */
function SiteConflictLines({
  summaries,
  when,
}: {
  summaries: SiteConflictSummary[];
  when: 'before' | 'after';
}): React.JSX.Element {
  return (
    <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {summaries.map((s) => (
        <li key={s.key}>
          {/* The TYPE is named because identity is `lower(name)::type`: one file may
              legitimately hold a ship and an office both called "Piraeus", each
              conflicted, and two identical-looking lines would leave the Manager
              acknowledging one while reading the other. */}
          <strong>{s.name}</strong> <span className="muted">({s.type})</span>
          {s.matched ? (
            <>
              {' '}
              is already in your fleet, so nothing from the file{' '}
              {when === 'before' ? 'will be' : 'was'} recorded for it:{' '}
              {s.attributes
                .map(
                  (a) =>
                    // `saved` is `record.won` on every matched Site, so the filter only
                    // satisfies the type — it drops nothing on this branch.
                    `${a.label} (${[a.saved, ...a.discarded]
                      .filter((v): v is string => v !== undefined)
                      .map(elide)
                      .join(', ')}${a.more > 0 ? ` and ${a.more} more` : ''})`,
                )
                .join('; ')}
              .
            </>
          ) : (
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {s.attributes.map((a) => (
                <li key={a.attribute}>
                  {/* No pick cannot happen (the seeding covers every conflicting
                      attribute of every unmatched Site) — but if it ever does, this
                      says so instead of inventing the first value, which is what
                      `siteCreate` will then refuse to commit. */}
                  {a.saved === undefined ? (
                    <>
                      {a.label}: <strong>no value chosen</strong> — Import will refuse this
                      site
                    </>
                  ) : (
                    <>
                      {a.label}: <strong>{elide(a.saved)}</strong>{' '}
                      {when === 'before' ? 'will be saved' : 'saved'}
                    </>
                  )}
                  {a.discarded.length > 0 && <> — {a.discarded.map(elide).join(', ')} discarded</>}
                  {/* The cap bounds what could be CHOSEN, so an unoffered value has to
                      be counted here too — otherwise the dialog reads as the complete
                      account of the contradiction when it is not. */}
                  {a.more > 0 && (
                    <>
                      {' '}
                      (<span data-numeric>{a.more}</span> more in the file, not offered)
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

type Step = 'upload' | 'map' | 'review' | 'done';

/** Field → its human label, derived from the one target-list definition. */
const FIELD_LABEL = new Map<ImportField, string>(IMPORT_FIELDS.map((f) => [f.field, f.label]));

/**
 * The chosen file's IDENTITY, held in wizard state (Story 8.4).
 *
 * It cannot live in the `<input>`: `onFile` clears `e.target.value` so the same file
 * can be re-picked, and a file input cannot be repopulated programmatically at all.
 * State is therefore what makes stepping back FREE — the name, the row count, the
 * read columns and the confirmed mapping all survive `Back to upload`, so returning
 * to the mapping costs no trip through the OS picker. Choosing a different file
 * replaces all of it; `reset()` clears all of it. Nothing is persisted: no storage,
 * no session carry-over.
 */
interface SelectedFile {
  name: string;
  /** `parsed.records.length` — data rows, which is what the wizard counts. */
  rowCount: number;
}

/**
 * How much of the preflight report is shown before it is summarised.
 *
 * Every cap is DISCLOSED where it bites (`and N more …`): a cap that silently hides
 * work is worse than no cap, because the Manager cannot tell a short list from a
 * truncated one. All three are logged in `deferred-work.md`.
 */
const MAX_REPORT_FAMILIES = 8;
const MAX_REPORT_VALUES = 3;
const MAX_REPORT_ROWS = 5;

/**
 * One CHECK's worth of exclusions — never one reason STRING's worth.
 *
 * Aggregating on {@link RowCheck} rather than on the rendered reason is what keeps the
 * report readable on the file that actually turns up: a 500-row export whose `Flag`
 * column holds ISO codes has 500 distinct reason strings and ONE check. `reason` is the
 * first row's full reason, so the string the mapping step shows is byte-identical to
 * the one the review step shows for that row (the one-validator invariant); the other
 * offending values are sampled in `values`.
 */
interface PreflightFamily {
  check: RowCheck;
  reason: string;
  rows: number;
  /** 1-based row numbers, capped — so the report is per-row detail, not only counts. */
  rowNumbers: number[];
  moreRows: number;
  /** Distinct offending values OTHER than the one `reason` already quotes, capped. */
  otherValues: string[];
  moreValues: number;
  vocabulary?: RowVocabulary;
}

/**
 * What {@link validateImportRow} says about the CONFIRMED mapping, before Continue
 * fetches anything (Story 8.4).
 *
 * `families` is ordered by row count descending — `families[0]` is the dominant check,
 * which is what a blocking message may name. Ties keep first-encounter order (the
 * validator's own check order), so the message is deterministic.
 */
interface Preflight {
  total: number;
  excluded: number;
  families: PreflightFamily[];
  /** Every closed vocabulary the report touches, de-duplicated, in first-seen order. */
  vocabularies: RowVocabulary[];
}

/**
 * Run the ONE row validator over the mapping's own output.
 *
 * This is the whole preflight: `applyMapping` + {@link validateImportRow}, with no
 * Directory data and therefore no network read — which is precisely why the report
 * can be shown HERE rather than after three `GET`s. Cost is bounded by `MAX_ROWS`
 * (5,000) × the mapped fields and is memoized on `[parsed, mapping]`, and a mapping
 * changes only on a deliberate `<select>` edit, never per keystroke.
 *
 * It deliberately computes no reason of its own: every string comes from the
 * validator, so this surface and the review step cannot disagree about a row.
 */
function preflightMapping(parsed: ParsedImportFile, mapping: ImportColumnMapping): Preflight {
  const rows = applyMapping(parsed, mapping);
  const byCheck = new Map<RowCheck, PreflightFamily>();
  // Every distinct value seen per check, so the `and N more` tail counts DISTINCT
  // overflow values rather than overflow rows.
  const seenValues = new Map<RowCheck, Set<string>>();
  const vocabularies: RowVocabulary[] = [];
  let excluded = 0;
  rows.forEach((row, i) => {
    const issues = validateImportRow(row);
    if (issues.length === 0) return;
    excluded += 1;
    // A row is counted once per CHECK it fails, so a row failing three checks appears
    // under three families — which is why the rendered report says outright that the
    // counts can sum above the excluded-row total.
    for (const issue of issues) {
      if (issue.vocabulary && !vocabularies.includes(issue.vocabulary)) {
        vocabularies.push(issue.vocabulary);
      }
      let known = byCheck.get(issue.check);
      if (!known) {
        known = {
          check: issue.check,
          reason: issue.reason,
          rows: 0,
          rowNumbers: [],
          moreRows: 0,
          otherValues: [],
          moreValues: 0,
          ...(issue.vocabulary ? { vocabulary: issue.vocabulary } : {}),
        };
        byCheck.set(issue.check, known);
        // The representative row's own value is already quoted inside `reason`, so it
        // is pre-seeded as seen and never re-sampled below.
        seenValues.set(issue.check, new Set(issue.value === undefined ? [] : [issue.value]));
      }
      known.rows += 1;
      if (known.rowNumbers.length < MAX_REPORT_ROWS) known.rowNumbers.push(i + 1);
      else known.moreRows += 1;
      const value = issue.value;
      const seen = seenValues.get(issue.check)!;
      if (value === undefined || seen.has(value)) continue;
      seen.add(value);
      if (known.otherValues.length < MAX_REPORT_VALUES) known.otherValues.push(value);
      else known.moreValues += 1;
    }
  });
  return {
    total: rows.length,
    excluded,
    families: [...byCheck.values()].sort((a, b) => b.rows - a.rows),
    vocabularies,
  };
}

/** A neutral text tag (never color-only) reused for status-like labels. */
function TextTag({ label, tone = 'neutral' }: { label: string; tone?: 'neutral' | 'ok' | 'bad' }): React.JSX.Element {
  return (
    <span className="status-pill" data-tone={tone}>
      <span className="status-pill__dot" aria-hidden="true" />
      {label}
    </span>
  );
}

/**
 * The three Directory reads the map step's Continue depends on, with the failure
 * labelled AT THE SOURCE.
 *
 * The label has to be attached here rather than in the caller's catch: the caller
 * also runs `applyMapping` and `classifyImport`, and a catch spanning all three
 * can only *assume* which one failed. Every fetch helper (and a dropped
 * connection) rejects with an `Error`, so an `instanceof` split cannot tell them
 * apart either — a bare re-throw would leave the Manager staring at `HTTP 500`
 * with no hint of what was being loaded, and a blanket Directory label would send
 * them to retry a network read over a parse bug.
 */
async function loadDirectory() {
  try {
    // `getSites(true)` — the full roster INCLUDING deactivated (DEV-4424 d). This is the
    // one caller that must see them, and it is the opposite of the reason every other
    // caller must not: the matcher's job is to RECOGNISE the vessel a row names, not to
    // offer it. Active-only here would make a row naming a mothballed vessel classify
    // `unmatched`, so the commit would send `create` and — `site` has no unique constraint
    // on (company_id, name) — silently fork the fleet into two same-named vessels, leaving
    // the original's reactivation no longer a restoration. Matching it instead routes the
    // row into the API's existing `Site is deactivated: <name>` 409, which is what the
    // spec asks for: the row fails and the commit says which one.
    return await Promise.all([getVendors(), getDevices(), getSites(true)]);
  } catch (err: unknown) {
    const detail = err instanceof Error ? err.message.trim() : '';
    throw new Error(
      detail
        ? `Couldn't load your Directory to match against: ${detail}`
        : "Couldn't load your Directory to match against. Try again.",
    );
  }
}

/**
 * Compliance Manager cockpit → Import (Story 2.7, FR-27, AR-19). A staged,
 * non-destructive bulk-import wizard. The Manager's OWN CSV/XLSX/JSON file is
 * parsed CLIENT-SIDE into canonical rows, each distinct Vendor/Device/Site is
 * auto-matched against the caller's visible global Directory, and a review step
 * surfaces matched (linked) / unmatched (create-as-proposed) / ambiguous (pick a
 * candidate) entities plus excluded rows — never silently dropped, never
 * auto-created.
 *
 * Story 7.4 inserts a **Map columns** step between Upload and Review: every
 * column detected in the file is listed with a suggested target field and a
 * per-column override (including "— ignore —"), so a header the alias table does
 * not know is disclosed and repointable instead of being discarded invisibly.
 * The CONFIRMED mapping — held in component state only, never persisted — is
 * what parses the rows that feed classification and commit.
 *
 * On confirm, one fully-resolved plan is posted to
 * `POST /api/v1/imports`, which applies it atomically and re-validates every
 * reference server-side (an invisible reference or invalid create rolls the whole
 * import back). The API never receives file bytes.
 */
export function ImportWizard(): React.JSX.Element {
  const [step, setStep] = useState<Step>('upload');
  // Story 7.4 — the read file and the Manager's confirmed column mapping. Both
  // live here and nowhere else: no storage, no per-user memory, no carry-over
  // between files.
  const [parsed, setParsed] = useState<ParsedImportFile | null>(null);
  const [mapping, setMapping] = useState<ImportColumnMapping>({});
  // Story 8.4 — which file this is. Held here for the same reason as the mapping:
  // the `<input>` cannot be the source of truth (see {@link SelectedFile}).
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [classification, setClassification] = useState<ImportClassification | null>(null);
  // Resolution choices keyed by entity key.
  const [vendorCreate, setVendorCreate] = useState<Record<string, boolean>>({});
  const [vendorPick, setVendorPick] = useState<Record<string, string>>({});
  const [deviceCreate, setDeviceCreate] = useState<Record<string, boolean>>({});
  const [devicePick, setDevicePick] = useState<Record<string, string>>({});
  // DEV-4444 — which value wins where the file contradicted itself about a Site,
  // keyed `${site.key}::${attribute}`.
  //
  // UNLIKE `vendorPick`/`devicePick` this is SEEDED, not empty (QA round 2,
  // 2026-09-24). Those two start empty because an ambiguous match has no defensible
  // default — the wizard genuinely does not know which existing row was meant. A
  // contradicted attribute does have one: the file's first value, which is what every
  // other importer in the product already uses. The first fix left it empty on purpose
  // so that an unpicked conflict stayed falsy and BLOCKED Import; the product decision
  // removed that block, and an empty control that blocks nothing is an error state with
  // no upside. The safeguard is now the acknowledgement dialog, not an unreachable
  // button — see `onImportClick`.
  const [sitePick, setSitePick] = useState<Record<string, string>>({});
  // DEV-4444 — the conflicts as they stood at the moment of a SUCCESSFUL commit, kept
  // for the done screen. It cannot be re-derived there: `reset()` is one click away and
  // `result` is the server's counts, which say nothing about which value won.
  const [importedConflicts, setImportedConflicts] = useState<SiteConflictSummary[]>([]);
  /** DEV-4444 — whether the acknowledgement dialog is open over the review step. */
  const [confirmingConflicts, setConfirmingConflicts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  // One sweep per read file. Recomputing per column inside the render body would
  // re-scan up to 5,000 records for every column on every mapping change.
  const samples = useMemo(
    () => (parsed ? columnSamples(parsed) : new Map<string, string>()),
    [parsed],
  );
  // What to call each column, and whether the file left any without a distinct
  // header — one computation, so the label and the sentence explaining it agree.
  const labels = useMemo(() => columnLabels(parsed?.columns ?? []), [parsed]);
  const displayNames = labels.names;
  // Story 8.4 — the row-level preflight. Unlike `samples`/`labels` above (keyed on
  // `[parsed]`, precisely so a mapping change does NOT re-scan the records), this one
  // depends on the mapping and so re-runs on every mapping edit — deliberately, since
  // that is the input whose consequences it exists to report. Keyed on
  // `[parsed, mapping]` so it re-runs on a deliberate mapping edit and on nothing
  // else; the cost is logged as an accepted boundary in `deferred-work.md`.
  const preflight = useMemo(
    () => (parsed ? preflightMapping(parsed, mapping) : null),
    [parsed, mapping],
  );
  // DEV-4444 — the conflicted Sites and their resolutions. Computed here, above the
  // handlers AND the review render, because three consumers need it: the click
  // handler (does a dialog open at all), the dialog itself, and the done screen.
  const conflictSummaries = useMemo(
    () => (classification ? siteConflictSummary(classification.sites, sitePick) : []),
    [classification, sitePick],
  );
  /**
   * Whether any contradicted value will actually be WRITTEN.
   *
   * False when every conflicted Site already exists: the per-site lines then correctly
   * say nothing is recorded, and every sentence wrapped around them has to agree. A
   * lead reading "Importing saves one value per field" above a list saying nothing is
   * saved is the same class of contradiction this ticket is about, one layer up.
   */
  const conflictsWillWrite = conflictSummaries.some((s) => !s.matched);

  function reset(): void {
    setStep('upload');
    setParsed(null);
    setMapping({});
    setSelectedFile(null);
    setClassification(null);
    setVendorCreate({});
    setVendorPick({});
    setDeviceCreate({});
    setDevicePick({});
    setSitePick({});
    setImportedConflicts([]);
    setConfirmingConflicts(false);
    setSubmitting(false);
    setAnalyzing(false);
    setError(null);
    setResult(null);
  }

  /**
   * Read the file and suggest a mapping, then hand over to the Map-columns step.
   * Parsing is local and instant, so this deliberately does NOT fetch the
   * Directory — a Manager who abandons at the mapping step never pays for three
   * network reads (they happen on Continue instead).
   */
  async function onFile(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file later. This is also why the input can never
    // be the source of truth for "which file is selected" (Story 8.4).
    e.target.value = '';
    if (!file) return;
    setError(null);
    setAnalyzing(true);
    try {
      const p = await readImportFile(file);
      // Replace everything the PREVIOUS file left behind — but only now that the new
      // one has actually been read. Clearing before the read would mean a mistyped
      // `.docx` costs the Manager the file they had already mapped, in a story whose
      // whole point is that a trip back to upload costs nothing. If the new pick never
      // took effect, the previous file is still the selected one and naming it (with
      // its mapping intact) is the honest thing to show.
      setParsed(p);
      setMapping(suggestMapping(p.columns));
      setSelectedFile({ name: file.name, rowCount: p.records.length });
      // The old classification and its resolution picks belong to the old file.
      setClassification(null);
      setVendorCreate({});
      setVendorPick({});
      setDeviceCreate({});
      setDevicePick({});
      setSitePick({});
      setImportedConflicts([]);
      // Cleared here and in `onConfirmMapping`/`backToUpload` as well as in `reset()`.
      // Only ConfirmModal's focus trap currently stops a Manager from reaching a
      // step-changing control while it is open, so leaving the flag set would make
      // this wizard's correctness depend on another component's a11y behaviour — and
      // a stale `true` lands the acknowledgement dialog on a classification the
      // Manager has not looked at yet.
      setConfirmingConflicts(false);
      setStep('map');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not read the file.');
    } finally {
      setAnalyzing(false);
    }
  }

  /**
   * Map → Review: parse the rows through the CONFIRMED mapping, fetch the
   * caller's visible Directory (RLS-scoped) and classify — same
   * fetch-all-then-narrow pattern as VendorDirectory/DeviceDirectory. Re-running
   * it after a Back from review reclassifies from the corrected mapping, so the
   * resolution choices are rebuilt from scratch rather than carried over stale.
   */
  async function onConfirmMapping(): Promise<void> {
    if (!parsed) return;
    setError(null);
    setAnalyzing(true);
    try {
      const rows = applyMapping(parsed, mapping);
      const [vendors, devices, sites] = await loadDirectory();
      const c = classifyImport(rows, vendors, devices, sites);
      // Defaults: unmatched entities are created by default; ambiguous start
      // unresolved (Import stays blocked until the Manager picks).
      const vc: Record<string, boolean> = {};
      for (const v of c.vendors) if (v.match === 'unmatched') vc[v.key] = true;
      const dc: Record<string, boolean> = {};
      for (const d of c.devices) if (d.match === 'unmatched') dc[d.key] = true;
      // DEV-4444 — and a contradicted Site attribute defaults to the file's FIRST
      // value, which is what `classifyImport` recorded as `won`. Seeded HERE rather
      // than in the control's `value` fallback for the reason `vc`/`dc` are: the
      // commit path reads this map, so a default that lives only in the rendered
      // `<select>` would commit as undefined the moment the Manager never touches it.
      // Re-running this on a Back-to-mapping Continue is also what discards a stale
      // pick — it is rebuilt from the re-classification, never carried over.
      const sp: Record<string, string> = {};
      for (const s of c.sites) {
        if (s.match === 'matched') continue; // no control, nothing to commit
        for (const attribute of siteConflicts(s)) {
          sp[`${s.key}::${attribute}`] = s.conflicts![attribute]!.won;
        }
      }
      setVendorCreate(vc);
      setDeviceCreate(dc);
      setVendorPick({});
      setDevicePick({});
      setSitePick(sp);
      setImportedConflicts([]);
      setConfirmingConflicts(false);
      setClassification(c);
      setStep('review');
    } catch (err: unknown) {
      // `loadDirectory` has already labelled the only failure the Manager can act
      // on. Anything else reaching here is a bug in `applyMapping`/`classifyImport`
      // — it must NOT borrow the Directory's wording, or the Manager retries a
      // network read that was never the problem.
      setError(err instanceof Error ? err.message : 'Could not prepare the review. Try again.');
    } finally {
      setAnalyzing(false);
    }
  }

  /**
   * Back out of the mapping step — WITHOUT throwing the file away (Story 8.4).
   *
   * Discarding `parsed`/`mapping` here was the defect: a file input cannot be
   * repopulated, so the Manager was sent back to the OS picker to re-locate a file
   * the wizard had already read, losing every mapping choice on the way. The upload
   * step now names the still-selected file and offers a route straight back to the
   * mapping. Replacing the file is what clears it (`onFile`), and so is `reset()`.
   */
  function backToUpload(): void {
    setError(null);
    // Unlike `onFile`/`onConfirmMapping` this one re-seeds nothing, so a surviving
    // flag would open the dialog over whatever classification is reached next.
    setConfirmingConflicts(false);
    setStep('upload');
  }

  // ---- Commit-plan builders (resolve choices → ImportCommitRequest) ----
  function vendorRef(v: ClassifiedVendor): ImportCommitVendor | null {
    if (v.match === 'matched') return { existingVendorId: v.existingId! };
    if (v.match === 'ambiguous') {
      const id = vendorPick[v.key];
      return id ? { existingVendorId: id } : null;
    }
    if (vendorCreate[v.key] === false) return null; // unchecked → skip
    return {
      propose: {
        name: v.name,
        ...(v.country ? { country: v.country } : {}),
        ...(v.category ? { category: v.category } : {}),
      },
    };
  }

  function deviceRef(d: ClassifiedDevice, vendors: Map<string, ClassifiedVendor>): ImportCommitDevice | null {
    if (d.match === 'matched') return { existingDeviceId: d.existingId! };
    if (d.match === 'ambiguous') {
      const id = devicePick[d.key];
      return id ? { existingDeviceId: id } : null;
    }
    if (deviceCreate[d.key] === false) return null; // unchecked → skip
    const mfr = vendors.get(d.vendorKey);
    const mfrRef = mfr ? vendorRef(mfr) : null;
    if (!mfrRef) return null; // manufacturer skipped/unresolved → device auto-skipped
    return {
      propose: {
        model: d.model,
        // Already canonical from parse; this re-narrows it to the closed type.
        ...(normalizeDeviceCategory(d.category) ? { category: normalizeDeviceCategory(d.category)! } : {}),
        // Story 7.1: carried only when present, so a version-less device commits
        // without a `version` key at all (never '' or null).
        ...(d.version ? { version: d.version } : {}),
        vendor: mfrRef,
      },
    };
  }

  /**
   * The create payload for an unmatched Site, with the Manager's conflict resolutions
   * applied over what `classifyImport` recorded (DEV-4444).
   *
   * The sibling of `vendorRef`/`deviceRef`, and it exists for the same reason: a
   * resolution choice lives in its own keyed state, never written back into the
   * classification, and is layered on HERE at commit time. Before this the site branch
   * passed `s.create!` straight through, so there was no hook at which a chosen value
   * could reach the wire.
   *
   * Every option came from a row that already passed `validateImportRow`, so a pick is
   * accepted by the server's `validateSiteCreate` unchanged — which is why the casts
   * below are safe and why this needs no re-validation of its own.
   */
  function siteCreate(s: ClassifiedSite): CreateSiteRequest {
    const base = s.create!;
    if (!s.conflicts) return base;
    const picked: CreateSiteRequest = { ...base };
    for (const attribute of SITE_ATTRIBUTES_BY_TYPE[s.type]) {
      if (!s.conflicts[attribute]) continue;
      const choice = sitePick[`${s.key}::${attribute}`];
      // An unpicked conflict cannot reach here: `onConfirmMapping` seeds every
      // conflicting attribute of every unmatched Site, and the control offers no
      // empty option to go back to. What guarantees this changed in QA round 2 — it
      // was the `unresolved` gate, it is now the seeding — so the refusal stays, and
      // deliberately does NOT fall back to `record.won`. Committing "the value
      // classifyImport happened to see first" is precisely the defect DEV-4444
      // removed; a silent fallback would make its return indistinguishable from
      // correct behaviour. `onImport` turns the throw into the Manager's error alert.
      if (!choice) {
        throw new Error(
          `Unresolved conflict for site "${s.name}" (${SITE_ATTRIBUTE_LABELS[attribute]}). Pick a value before importing.`,
        );
      }
      switch (attribute) {
        case 'imo':
          picked.imo = choice;
          break;
        case 'flagState':
          picked.flagState = choice as FlagState;
          break;
        case 'vesselType':
          picked.vesselType = choice as VesselType;
          break;
        case 'city':
          picked.city = choice;
          break;
      }
    }
    return picked;
  }

  /**
   * The Import button's handler (DEV-4444, QA round 2).
   *
   * A file that contradicts itself about a Site does not commit on this click — it
   * opens the acknowledgement dialog first. A file that does not goes straight to
   * `onImport`, so the ordinary import path is exactly what it was: an interstitial
   * on every import would be a nag, and it is the CONTRADICTION that earns one.
   *
   * The dialog covers a MATCHED conflicting Site too, even though nothing from the
   * file is written to it. That is the case QA actually reproduced: the inline note
   * exists, but a Manager scanning a long review list gets no interruption at all on
   * the one path where the file's values are discarded wholesale.
   */
  function onImportClick(): void {
    if (conflictSummaries.length > 0) {
      setError(null);
      setConfirmingConflicts(true);
      return;
    }
    void onImport();
  }

  async function onImport(): Promise<void> {
    if (!classification) return;
    setError(null);
    setSubmitting(true);
    try {
      const vendorsByKey = new Map(classification.vendors.map((v) => [v.key, v]));
      const sites: ImportCommitSite[] = classification.sites.map((s) => {
        const vendors = s.vendorKeys
          .map((k) => vendorsByKey.get(k))
          .filter((v): v is ClassifiedVendor => v !== undefined)
          .map((v) => vendorRef(v))
          .filter((r): r is ImportCommitVendor => r !== null);
        const devices = s.deviceKeys
          .map((k) => classification.devices.find((d) => d.key === k))
          .filter((d): d is ClassifiedDevice => d !== undefined)
          .map((d) => deviceRef(d, vendorsByKey))
          .filter((r): r is ImportCommitDevice => r !== null);
        return s.match === 'matched'
          ? { existingSiteId: s.existingId!, vendors, devices }
          : { create: siteCreate(s), vendors, devices };
      });
      const req: ImportCommitRequest = { sites };
      const res = await commitImport(req);
      setResult(res);
      // Snapshot what was acknowledged, for the done screen. Taken on SUCCESS only —
      // a failed commit leaves the dialog open over an unchanged review step, and a
      // done screen is never reached to read it.
      setImportedConflicts(conflictSummaries);
      setConfirmingConflicts(false);
      setStep('done');
    } catch (err: unknown) {
      // The dialog (when open) stays open and renders this inside itself — the
      // ConfirmModal contract, and the review step behind the scrim is not readable
      // anyway.
      setError(err instanceof Error ? err.message : "Couldn't import. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Upload step ----
  if (step === 'upload') {
    return (
      <>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Import</h1>
        <section className="card stack" aria-labelledby="import-upload-heading">
          <h2 id="import-upload-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Import Sites &amp; Devices
          </h2>
          <p className="muted" style={{ margin: 0 }}>
            Upload a CSV, XLSX/XLS, or JSON file (up to 5&nbsp;MB / 5,000 rows / 500 columns). It is parsed in your
            browser and matched against your Directory — you review every match before anything is saved.
          </p>
          <details style={{ fontSize: 13 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
              Which columns are recognized?
            </summary>
            <div className="stack muted" style={{ gap: 8, marginTop: 8 }}>
              <p style={{ margin: 0 }}>
                The headers below are the <em>complete</em> list of what is recognized automatically
                (matching ignores case and leading/trailing spaces). Everything else is <em>not</em>{' '}
                guessed at: after you choose a file, a <strong>Map columns</strong> step lists every
                column found in it, so you can point any header at any field — or leave it out on
                purpose. No column is dropped without being shown to you first. One row per Site; a
                row may also name a Vendor and a Device to link.
              </p>
              {/* GENERATED from HEADER_ALIASES (Story 8.4 AC3). The hand-written list
                  that used to sit here named a hand-picked subset and omitted real
                  aliases (`Site Type`, `Vendor Name`, `Device Model`, `Flag State`,
                  `site_name`, …), so the one surface whose job is to describe the
                  alias table described a smaller, different one. Deriving it is what
                  makes "describe the table that exists" true by construction — a new
                  alias needs no edit here. Do not re-hand-write it. */}
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                {/* The group list is DERIVED too, in `HEADER_ALIAS_HELP`'s own order.
                    A hand-written `['Sites','Vendors','Devices'] satisfies
                    readonly ImportFieldGroup[]` catches a renamed group but NOT a new
                    one — `satisfies` proves the names are valid, never that they cover
                    that union — and the alias-coverage test cannot catch it either,
                    since a brand-new field's group would have no alias yet. That is
                    the silent-omission failure AC3 exists to end, so nothing here is
                    hand-maintained: a field in a new group appears under it. */}
                {[...new Set(HEADER_ALIAS_HELP.map((f) => f.group))].map((group) => (
                  <li key={group}>
                    {/* No group-level "(optional)": under Sites only Site Name is
                        required outright, and the other five are conditionally
                        required (a ship needs its IMO/Flag/Vessel Type, an office its
                        City) — which is exactly what the group label used to hide.
                        Requiredness is stated per FIELD instead. */}
                    <strong>{group}</strong>:{' '}
                    {HEADER_ALIAS_HELP.filter((f) => f.group === group).map((f, i) => (
                      <span key={f.field}>
                        {i > 0 ? ' · ' : ''}
                        {f.label}
                        {f.required ? ' (required)' : f.note ? ` (${f.note})` : ''}
                        {' — '}
                        {f.aliases.length > 0 ? (
                          <>
                            {f.aliases
                              .filter((alias) => !f.weakAliases.includes(alias))
                              .map((alias, j) => (
                                <span key={alias}>
                                  {j > 0 ? ', ' : ''}
                                  <code>{alias}</code>
                                </span>
                              ))}
                            {/* Rendering all eight device-version spellings as equals
                                would tell the Manager `Rev` and `Device Version` are
                                interchangeable — the precedence the weak set exists to
                                deny. Derived from `WEAK_HEADER_ALIASES`, so a future
                                demotion needs no edit here either. */}
                            {f.weakAliases.length > 0 && (
                              <>
                                {' — or, only when no qualified column supplies a value: '}
                                {f.weakAliases.map((alias, j) => (
                                  <span key={alias}>
                                    {j > 0 ? ', ' : ''}
                                    <code>{alias}</code>
                                  </span>
                                ))}
                              </>
                            )}
                          </>
                        ) : (
                          <em>no recognized header — map it by hand</em>
                        )}
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
              <p style={{ margin: 0 }}>
                A <strong>Type</strong> must read <code>ship</code> or <code>office</code>. A ship row
                also needs a 7-digit <strong>IMO</strong> and a <strong>Flag</strong> and{' '}
                <strong>Vessel Type</strong> the platform knows; an office row needs a{' '}
                <strong>City</strong>. The Map columns step names the accepted values for whichever of
                those a file gets wrong, before anything is loaded.
              </p>
              <p style={{ margin: 0 }}>
                A Vendor or Device not already in your Directory can be created as a{' '}
                <em>proposed</em> entry from the review step — nothing is saved until you confirm.
              </p>
            </div>
          </details>
          <label className="stack" style={{ gap: 4, alignItems: 'flex-start' }}>
            <span className="field-label">File</span>
            <input
              type="file"
              accept=".csv,.xlsx,.xls,.json"
              aria-label="Import file"
              disabled={analyzing}
              onChange={(e) => void onFile(e)}
              style={fieldStyle}
            />
          </label>
          {analyzing && (
            <p className="muted" style={{ margin: 0 }}>
              Reading the file…
            </p>
          )}
          {/* Story 8.4 — the file survived "Back to upload", so say so and offer the
              way back. Without this the Manager is looking at an empty file input
              (which cannot be repopulated) with no sign that their columns, their
              mapping and their overrides are all still here. */}
          {selectedFile && parsed && !analyzing && (
            <div className="stack" style={{ gap: 8, alignItems: 'flex-start' }}>
              <p className="muted" style={{ margin: 0 }}>
                Still selected: <strong>{selectedFile.name}</strong> —{' '}
                <span data-numeric>{selectedFile.rowCount}</span>{' '}
                {selectedFile.rowCount === 1 ? 'data row' : 'data rows'}. Your column mapping is kept;
                {/* "a file", not "another file". The input's value is cleared after every
                    read, so re-picking the SAME file fires `change` again and re-suggests
                    the mapping like any other pick — the promise has to cover that
                    gesture, since it is the most natural one on a step whose visible
                    affordance is a file input. */}{' '}
                picking a file above replaces it.
              </p>
              <button
                type="button"
                className="btn-accent"
                data-surface="accent"
                onClick={() => {
                  setError(null);
                  setStep('map');
                }}
              >
                Back to mapping
              </button>
            </div>
          )}
          <div role="alert" aria-live="assertive" style={{ minHeight: 16 }}>
            {error && <TextTag label={error} tone="bad" />}
          </div>
        </section>
      </>
    );
  }

  // ---- Map-columns step (Story 7.4) ----
  if (step === 'map' && parsed) {
    // The winner-per-field view the parser itself will use, so what is disclosed
    // here and what actually parses cannot disagree.
    const byField = mappedColumnsByField(parsed.columns, mapping);
    const ignoredColumns = parsed.columns.filter((c) => !mappingTarget(mapping, c.key));
    const duplicates = [...byField.entries()].filter(([, cols]) => cols.length > 1);
    // A file with no columns has nothing to map; a file with no Site Name column
    // would turn EVERY row into a "missing site name" error one step later.
    //
    // Covering the field is not enough: a column that is blank on EVERY row
    // satisfies `byField` and still produces that same all-rows-fail import, one
    // Directory round-trip too late. `samples` already knows — it is what renders
    // "no values in this column" beside that very select — so the gate consults
    // it rather than re-deriving.
    const siteNameColumns = byField.get('siteName') ?? [];
    const siteNameIsEmpty =
      siteNameColumns.length > 0 && !siteNameColumns.some((c) => samples.has(c.key));
    // Story 8.4 — the LAST arm, so the three specific messages above keep priority:
    // a message naming the offending column outranks a generic count. "Continue" has
    // to mean the import will produce rows, so a mapping under which NO row survives
    // is a block; a partial one is warned about and stays continuable (Epic 7's UAT
    // confirmed the partial import as intended behaviour).
    const nothingSurvives =
      preflight !== null && preflight.total > 0 && preflight.excluded === preflight.total;
    const dominant = preflight?.families[0];
    // Only a check accounting for MOST of the excluded rows may be presented as the
    // explanation. With per-value reasons the highest count is often 1, and
    // "All 500 rows would be excluded: flag state "GR" … (1 of 500)" states two numbers
    // that contradict each other. When no check has a majority the sentence says so and
    // sends the Manager to the list.
    //
    // The denominator is `excluded`, not `total`: "most of the EXCLUDED rows fail this
    // check" is the claim the sentence makes. Today the only caller is the
    // `nothingSurvives` arm, where the two are equal by definition — naming the right
    // one anyway means naming the dominant reason in the partial case (which the report
    // already has the data for) cannot silently start under-reporting dominance.
    const dominates =
      dominant !== undefined && preflight !== null && dominant.rows * 2 > preflight.excluded;
    const specificBlock =
      parsed.columns.length === 0
        ? 'No columns were detected in this file. Go back and upload a file with a header row.'
        : siteNameColumns.length === 0
          ? 'Map a column to Site Name before continuing — every row needs one.'
          : siteNameIsEmpty
            ? `The column mapped to Site Name (${siteNameColumns
                .map((c) => displayNames.get(c.key) ?? c.header)
                .join(', ')}) is empty on every row — map Site Name to a column that has values.`
            : null;
    const blockedReason =
      specificBlock ??
      (nothingSurvives && dominant
        ? `Nothing would be imported under this mapping — fix the mapping or the file before continuing. ${
            dominates
              ? // `dominant.reason` is the FIRST failing row's, so it quotes THAT row's
                // cell. Where the family holds other values too, presenting it as the
                // explanation for all of them states something false: six rows carrying
                // six ISO codes would read `Most excluded rows fail because flag state
                // "GR" is not a recognised value` on the strength of one row. The claim
                // is about the CHECK, so where the values differ the sentence says so
                // and hands off to the bullet that lists them.
                dominant.otherValues.length === 0 && dominant.moreValues === 0
                ? `Most excluded rows fail because ${dominant.reason}.`
                : `Most excluded rows fail the same check — ${dominant.reason}, and the other values listed below.`
              : `No single problem accounts for most of them; each is listed below.`
          }`
        : null);
    // The report's own heading. It is ALWAYS rendered when there is anything to report,
    // so the bullet list is never an unheaded list of counts sitting above the sentence
    // that explains it — and the dominant check's count appears only in its bullet,
    // never a second time in a different format inside the blocking sentence.
    // A file with NO detected columns is the one state where the report has nothing to
    // say: there is no mapping to fix, so "all 2 rows would be excluded — missing site
    // name … Accepted site types: ship · office" points at `<select>`s that do not
    // exist and states a vocabulary for a column the file does not contain. Its block
    // above is the whole actionable message (go back and upload a file with a header
    // row). Every OTHER block keeps the report beside it — with a Site Name column
    // still unmapped, the report is the only thing naming the other fields the file
    // also fails, and suppressing it turns the story's one local report back into the
    // serial discover-one-blocker-at-a-time loop it exists to collapse.
    const reportIntro =
      preflight === null || preflight.excluded === 0 || parsed.columns.length === 0
        ? null
        : nothingSurvives
          ? `All ${preflight.total} ${preflight.total === 1 ? 'row' : 'rows'} would be excluded by this mapping:`
          : `${preflight.excluded} of ${preflight.total} ${preflight.total === 1 ? 'row' : 'rows'} will be excluded from this import:`;
    // The families actually on screen, and the vocabularies belonging to THEM. Stating
    // "Accepted vessel types: …" beside no bullet mentioning a vessel type is a
    // vocabulary floating free of any problem — the family cap must take its
    // vocabularies with it, or the cap leaks past the one thing it is allowed to trim.
    const shownFamilies = preflight?.families.slice(0, MAX_REPORT_FAMILIES) ?? [];
    const shownVocabularies =
      preflight?.vocabularies.filter((v) => shownFamilies.some((f) => f.vocabulary === v)) ?? [];

    return (
      <>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Import</h1>
        <section className="card stack" aria-labelledby="import-map-heading">
          <h2 id="import-map-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Map columns
          </h2>
          {/* Story 8.4 — WHICH file this is. A Manager working through several
              similar exports had nothing on screen telling them apart. Present on
              arrival and unchanged by any control, so it belongs here rather than in
              the live region below. */}
          {selectedFile && (
            <p className="muted" style={{ margin: 0 }}>
              From <strong>{selectedFile.name}</strong> — <span data-numeric>{selectedFile.rowCount}</span>{' '}
              {selectedFile.rowCount === 1 ? 'data row' : 'data rows'}.
            </p>
          )}
          {/* Content present on ARRIVAL, so it belongs here and not in the
              role="status" region below: an aria-live region must not hold static
              text, or a screen reader announces it as a change that never
              happened. */}
          <p className="muted" style={{ margin: 0 }}>
            Every column found in your file is listed below with the field we think it means. Change
            any of them, or leave one as “{IGNORE_LABEL}” to keep it out of the import — nothing is
            decided about a column without showing it to you here.
            {/* Deliberately says neither "in your file" nor "in the list below":
                the number is `ImportColumn.position`, which is the file's own
                column number for a single-sheet file but UNION order across a
                multi-sheet workbook, and can skip a number where a phantom padding
                column was dropped. "Its column number" is true on every path. */}
            {labels.anyIndistinct && (
              <>
                {' '}
                Where a column has no header of its own, or shares one with another column, it is
                told apart by its column number, shown in parentheses.
              </>
            )}
          </p>

          {parsed.columns.length > 0 && (
            <ul
              style={{
                margin: 0,
                padding: 0,
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {parsed.columns.map((column) => {
                const sample = samples.get(column.key) ?? '';
                const name = displayNames.get(column.key) ?? column.header;
                return (
                  <li
                    key={column.key}
                    style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <span style={{ minWidth: 190 }}>
                      <strong>{name}</strong>
                      <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                        {/* Not `data-numeric`: a sample is arbitrary cell text,
                            and the mono face is reserved for numbers, dates and
                            identifiers. */}
                        {sample ? <>e.g. {sample}</> : 'no values in this column'}
                      </span>
                    </span>
                    <select
                      aria-label={`Map column ${name}`}
                      value={mappingTarget(mapping, column.key)}
                      disabled={analyzing}
                      onChange={(e) => {
                        // A failed Continue leaves an assertive alert naming an
                        // attempt that no longer matches the on-screen mapping;
                        // editing the mapping is what makes it stale.
                        setError(null);
                        setMapping((m) => ({
                          ...m,
                          [column.key]: e.target.value as ImportField | '',
                        }));
                      }}
                      style={fieldStyle}
                    >
                      {IMPORT_FIELDS.map((f) => (
                        <option key={f.field} value={f.field}>
                          {f.required ? `${f.label} (required)` : f.label}
                        </option>
                      ))}
                      <option value="">{IGNORE_LABEL}</option>
                    </select>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Both disclosures change in response to a <select> edit, exactly like
              the blocking reason below, so they are announced too. The region is
              mounted unconditionally and only its CONTENT changes: a role="status"
              element that first appears together with its text is not announced
              (same reason as DeviceDirectory's result count). */}
          <div role="status" aria-live="polite" className="stack" style={{ gap: 4 }}>
            {/* AR-19: what will NOT be imported is stated outright, with a count. */}
            {parsed.columns.length > 0 && (
              <p className="muted" style={{ margin: 0 }}>
                <span data-numeric>{ignoredColumns.length}</span>{' '}
                {ignoredColumns.length === 1 ? 'column' : 'columns'} will not be imported
                {ignoredColumns.length > 0 ? (
                  <>: {ignoredColumns.map((c) => displayNames.get(c.key) ?? c.header).join(', ')}</>
                ) : null}
                .
              </p>
            )}

            {duplicates.length > 0 && (
              <>
                <p className="muted" style={{ margin: 0 }}>
                  Some fields are covered by more than one column:
                </p>
                <ul className="muted" style={{ margin: 0, paddingLeft: 18 }}>
                  {duplicates.map(([field, cols]) => (
                    <li key={field}>
                      {FIELD_LABEL.get(field) ?? field}:{' '}
                      <strong>
                        {cols[0] ? (displayNames.get(cols[0].key) ?? cols[0].header) : ''}
                      </strong>{' '}
                      wins, then{' '}
                      {cols
                        .slice(1)
                        .map((c) => displayNames.get(c.key) ?? c.header)
                        .join(', ')}{' '}
                      — the first of them with a value on a row is used.
                    </li>
                  ))}
                </ul>
              </>
            )}

            {/* The blocking reason lives in the SAME region, because
                `aria-describedby` alone announces nothing here: a `disabled`
                button is out of the a11y tree and cannot be focused to read its
                own description. One region, so the step has one status voice.
                It comes BEFORE the row report, so a list of counts is never read
                out ahead of the sentence explaining why Continue is dead. */}
            {blockedReason && (
              <p id="import-map-blocked" className="muted" style={{ margin: 0 }}>
                {blockedReason}
              </p>
            )}

            {/* Story 8.4 — the ROW preflight: what this mapping would exclude, and
                why, computed from the one validator the review step uses and from no
                network read at all. Reported (not blocked) while some rows survive;
                the blocking arm is `blockedReason` above. Lives in this region for
                the same reason the duplicate disclosure does — it changes in response
                to a `<select>` edit.

                One line per CHECK, never per reason string: a not-recognised reason
                quotes the offending value, so 500 rows of ISO flag codes are 500
                distinct strings and one check. The other values are sampled, the row
                numbers are sampled, and every cap says how much it is hiding. */}
            {preflight && reportIntro && (
              <>
                <p className="muted" style={{ margin: 0 }}>
                  {reportIntro}
                </p>
                <ul className="muted" style={{ margin: 0, paddingLeft: 18 }}>
                  {shownFamilies.map((f) => (
                    <li key={f.check}>
                      {/* The reason is the FIRST failing row's, verbatim — the same
                          string the review step renders for that row. */}
                      {/* "data rows", the same words this step's file line uses: the
                          number is the record's position among the DATA rows, which is
                          not a spreadsheet row number — a header row shifts it by one,
                          and an XLSX's records are concatenated across every sheet
                          (AD-23), so "row 4" of a two-sheet workbook may live on the
                          second sheet. It is the same numbering the review step's row
                          errors use, so the two surfaces agree. */}
                      {f.reason} — <span data-numeric>{f.rows}</span>{' '}
                      {f.rows === 1 ? 'row' : 'rows'}, at data{' '}
                      {f.rowNumbers.length === 1 ? 'row' : 'rows'} {f.rowNumbers.join(', ')}
                      {f.moreRows > 0 ? <> and {f.moreRows} more</> : null}
                      {f.otherValues.length > 0 ? (
                        <>
                          {/* "for this field", not "in this column": a family
                              aggregates every row failing the check, and more than one
                              column can be mapped to the same field, so the values need
                              not all come from one column. */}
                          ; other values seen for this field:{' '}
                          {f.otherValues.map((v) => `“${v}”`).join(', ')}
                          {f.moreValues > 0 ? <> and {f.moreValues} more</> : null}
                        </>
                      ) : null}
                    </li>
                  ))}
                  {preflight.families.length > MAX_REPORT_FAMILIES && (
                    <li>
                      …and {preflight.families.length - MAX_REPORT_FAMILIES} more{' '}
                      {preflight.families.length - MAX_REPORT_FAMILIES === 1 ? 'problem' : 'problems'}{' '}
                      not listed here.
                    </li>
                  )}
                </ul>
                {/* A row can fail several checks, so the counts above are per PROBLEM
                    and can add up to more than the excluded-row count. Saying so is
                    cheaper than a Manager mis-reading three "1 row" lines as 3 rows. */}
                {preflight.families.length > 1 && (
                  <p className="muted" style={{ margin: 0 }}>
                    A row can fail more than one of these, so the counts above can add up to more
                    than <span data-numeric>{preflight.excluded}</span>.
                  </p>
                )}
                {/* A report the Manager cannot act on is not a report: where a
                    vocabulary is CLOSED, its values are stated. ONCE per vocabulary,
                    not once per line — 17 flag states repeated per line is what made
                    the pre-review draft unreadable on a real file. Only for the
                    families on screen: see `shownVocabularies`. */}
                {shownVocabularies.map((v) => (
                  <p key={v.name} className="muted" style={{ margin: 0 }}>
                    Accepted {v.name}: {v.values.join(' · ')}
                  </p>
                ))}
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn-accent"
              data-surface="accent"
              onClick={() => void onConfirmMapping()}
              disabled={analyzing || blockedReason !== null}
              // Tie the control to its reason for anyone who reaches it by other
              // means (voice control, a11y tree inspection). The live region
              // above is what actually announces the change.
              aria-describedby={blockedReason ? 'import-map-blocked' : undefined}
            >
              {analyzing ? 'Analyzing…' : 'Continue'}
            </button>
            <button type="button" className="icon-button" onClick={backToUpload} disabled={analyzing}>
              Back to upload
            </button>
          </div>
          {analyzing && (
            <p className="muted" style={{ margin: 0 }}>
              Analyzing… matching against your Directory.
            </p>
          )}
          <div role="alert" aria-live="assertive" style={{ minHeight: 16 }}>
            {error && <TextTag label={error} tone="bad" />}
          </div>
        </section>
      </>
    );
  }

  // ---- Done step ----
  if (step === 'done' && result) {
    return (
      <>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Import</h1>
        <section className="card stack" aria-labelledby="import-done-heading">
          <h2 id="import-done-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Import complete
          </h2>
          <p style={{ margin: 0 }}>
            Created <span data-numeric>{result.sitesCreated}</span>{' '}
            {result.sitesCreated === 1 ? 'site' : 'sites'} · linked{' '}
            <span data-numeric>{result.sitesLinked}</span> · proposed{' '}
            <span data-numeric>{result.vendorsProposed}</span>{' '}
            {result.vendorsProposed === 1 ? 'vendor' : 'vendors'} · assigned{' '}
            <span data-numeric>{result.vendorsAssigned}</span> · proposed{' '}
            <span data-numeric>{result.devicesProposed}</span>{' '}
            {result.devicesProposed === 1 ? 'device' : 'devices'} · installed{' '}
            <span data-numeric>{result.devicesInstalled}</span>.
          </p>
          {/* DEV-4444 (QA round 2) — "inform which rows we kept in the Import success
              screen". The dialog is a decision the Manager makes in a hurry and then
              dismisses; this is the record they can still read afterwards, and the
              only place a resolution survives the commit at all. */}
          {importedConflicts.length > 0 && (
            <div className="stack" style={{ gap: 8 }}>
              {/* "resolved" is a claim about a write, so it is only made when one
                  happened. A matched-only conflict resolved nothing — it discarded
                  everything the file said — and the heading has to say so. */}
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800 }}>
                {importedConflicts.some((s) => !s.matched)
                  ? 'Conflicting rows resolved'
                  : 'Conflicting rows discarded'}
              </h3>
              <p className="muted" style={{ margin: 0 }}>
                The file gave more than one value for{' '}
                {importedConflicts.length === 1 ? (
                  'this site'
                ) : (
                  <>
                    these <span data-numeric>{importedConflicts.length}</span> sites
                  </>
                )}
                :
              </p>
              <SiteConflictLines summaries={importedConflicts} when="after" />
            </div>
          )}
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={reset}
            style={{ alignSelf: 'flex-start' }}
          >
            Import another file
          </button>
        </section>
      </>
    );
  }

  // ---- Review step ----
  if (!classification) return <></>;
  const { sites, vendors, devices, rowErrors } = classification;
  const vendorsByKey = new Map(vendors.map((v) => [v.key, v]));

  const sitesToCreate = sites.filter((s) => s.match === 'unmatched').length;
  const sitesToLink = sites.filter((s) => s.match === 'matched').length;

  // Import is blocked while any ambiguous entity is unresolved (or submitting).
  //
  // A contradicted Site attribute is NOT among these (DEV-4444, QA round 2). The first
  // fix made it a third blocking condition; the product decision of 2026-09-24 replaced
  // that block with a default plus an acknowledgement, so an ambiguous MATCH is once
  // again the only thing that can disable this button. The two are genuinely different
  // problems: an ambiguous match has no defensible default — the wizard cannot know
  // which existing row was meant, and guessing links a device to the wrong vendor —
  // whereas a contradicted attribute has one, and the Manager can still override it.
  const unresolved =
    vendors.some((v) => v.match === 'ambiguous' && !vendorPick[v.key]) ||
    devices.some((d) => d.match === 'ambiguous' && !devicePick[d.key]);

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Import</h1>
        {/* A mis-mapped column is usually spotted HERE, so it has to be fixable
            here: the mapping choices are kept, and re-continuing reclassifies. */}
        {parsed && (
          <button
            type="button"
            className="icon-button"
            onClick={() => {
              setError(null);
              setStep('map');
            }}
            disabled={submitting}
          >
            Back to mapping
          </button>
        )}
      </div>

      {rowErrors.length > 0 && (
        <section className="card stack" aria-labelledby="import-excluded-heading">
          <h2 id="import-excluded-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Rows needing attention — excluded
          </h2>
          <p className="muted" style={{ margin: 0 }}>
            These rows are not importable and were left out. Fix them in your file and re-import.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {rowErrors.map((r) => (
              <li key={r.row}>
                Row <span data-numeric>{r.row}</span>
                {r.siteName ? <> ({r.siteName})</> : null}: {r.reason}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card stack" aria-labelledby="import-sites-heading">
        <h2 id="import-sites-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Sites
        </h2>
        <p className="muted" style={{ margin: 0 }}>
          <span data-numeric>{sitesToCreate}</span> to create · <span data-numeric>{sitesToLink}</span> to link
        </p>
        {sites.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No importable sites in this file.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sites.map((s) => {
              // DEV-4444 — the attributes the file gave more than one value for.
              const conflicts = siteConflicts(s);
              return (
                <li key={s.key} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {s.name} <span className="muted">({s.type})</span>{' '}
                  {s.match === 'matched' ? <TextTag label="Link existing" /> : <TextTag label="Create" tone="ok" />}
                  {conflicts.map((attribute) => {
                    const label = SITE_ATTRIBUTE_LABELS[attribute];
                    const record = s.conflicts![attribute]!;
                    const options = siteConflictOptions(s, attribute);
                    // On a MATCHED Site the file's values are never written — `create`
                    // is gone — so there is nothing to choose and a control would
                    // promise an effect it cannot have. State the contradiction and
                    // say plainly that none of it is recorded, the same fork the
                    // device notes make rather than claiming a value "is used"
                    // beside a line saying it is not.
                    if (s.match === 'matched') {
                      return (
                        <span key={attribute} className="muted" style={{ fontSize: 13 }}>
                          The file gave more than one {label} for this site (
                          {options.map(elide).join(', ')}
                          {record.more > 0 ? ` and ${record.more} more` : ''}); none is recorded — this
                          site is already in your fleet.
                        </span>
                      );
                    }
                    return (
                      <label
                        key={attribute}
                        style={{
                          display: 'inline-flex',
                          gap: 6,
                          alignItems: 'center',
                          // The row can hold three of these (a ship contradicted on all
                          // its attributes), each a full sentence plus a control. An
                          // inline-flex box does not wrap internally, so without these
                          // the row becomes one very wide unbreakable unit and the page
                          // scrolls horizontally at 1024px.
                          flexWrap: 'wrap',
                          maxWidth: '100%',
                        }}
                      >
                        <span className="muted">
                          The file disagrees about {label} — this will be saved:
                        </span>
                        {/* `value` has no `?? record.won` fallback: it would render the
                            default whether or not the seeding ran, which is what made
                            the seeding unobservable to every test that reads this
                            control. `sitePick` is the only source of truth. */}
                        <select
                          aria-label={`Resolve ${label} for site ${s.name}`}
                          value={sitePick[`${s.key}::${attribute}`] ?? ''}
                          onChange={(e) =>
                            setSitePick((m) => ({ ...m, [`${s.key}::${attribute}`]: e.target.value }))
                          }
                          style={fieldStyle}
                        >
                          {/* No `Select…` sentinel (QA round 2). It existed to hold the
                              control in a falsy state that BLOCKED Import; with no block
                              left, an empty option is just a way back into a state that
                              commits nothing and warns about nothing. */}
                          {/* The VALUE stays verbatim — it is what commits — while the
                              label is elided. A cell is arbitrary text and nothing
                              upstream caps its length, so a mis-mapped column can put
                              kilobytes in an office's City; the row-error surface
                              already elides for exactly this reason. */}
                          {options.map((v) => (
                            <option key={v} value={v}>
                              {elide(v)}
                            </option>
                          ))}
                        </select>
                        {/* The offered list is capped, so a Manager whose intended
                            value is not on it must be told the file held more —
                            otherwise a truncated list reads as the complete one. */}
                        {record.more > 0 && (
                          <span className="muted" style={{ fontSize: 13 }}>
                            and <span data-numeric>{record.more}</span> more in the file, not offered
                            here — fix the file if the right {label} is missing.
                          </span>
                        )}
                      </label>
                    );
                  })}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="card stack" aria-labelledby="import-vendors-heading">
        <h2 id="import-vendors-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Vendors
        </h2>
        {vendors.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No vendors in this file.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {vendors.map((v) => (
              <li key={v.key} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong>{v.name}</strong>
                {v.match === 'matched' && <TextTag label="Linked" />}
                {v.match === 'unmatched' && (
                  <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      aria-label={`Create vendor ${v.name} as proposed`}
                      checked={vendorCreate[v.key] ?? true}
                      onChange={(e) => setVendorCreate((m) => ({ ...m, [v.key]: e.target.checked }))}
                    />
                    Create as proposed
                  </label>
                )}
                {v.match === 'ambiguous' && (
                  <label  style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span className="muted">Multiple matches — pick one:</span>
                    <select
                      aria-label={`Resolve vendor ${v.name}`}
                      value={vendorPick[v.key] ?? ''}
                      onChange={(e) => setVendorPick((m) => ({ ...m, [v.key]: e.target.value }))}
                      style={fieldStyle}
                    >
                      <option value="">Select…</option>
                      {v.candidates?.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.id.slice(0, 8)})
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card stack" aria-labelledby="import-devices-heading">
        <h2 id="import-devices-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Devices
        </h2>
        {devices.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No devices in this file.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {devices.map((d) => {
              const mfr = vendorsByKey.get(d.vendorKey);
              // The device is auto-skipped because its manufacturer was UNCHECKED —
              // narrower than "the manufacturer is unresolved" (an ambiguous vendor
              // awaiting a pick is not auto-skipped; it blocks Import instead), so
              // this drives only the "Auto-skipped" tag below.
              const mfrSkipped =
                d.match === 'unmatched' &&
                mfr?.match === 'unmatched' &&
                vendorCreate[mfr.key] === false;
              // Story 7.1 — will this row's parsed version actually be WRITTEN?
              // Only a device committed as a NEW proposal carries one, so mirror
              // `deviceRef()`'s null conditions EXACTLY rather than approximating
              // them: it returns null for an ambiguous device with no pick, for an
              // unchecked "Create as proposed", and for ANY manufacturer that
              // `vendorRef()` cannot resolve — which is broader than "unchecked
              // vendor": an ambiguous manufacturer awaiting a pick resolves to null
              // too. Deriving this from `vendorRef` itself means the two can't drift.
              // `matched` (and a resolved `ambiguous`) LINK to a catalog row, and
              // import never edits an existing shared-backbone record. Getting this
              // wrong shows a value that silently goes nowhere.
              const mfrUnresolved = !mfr || vendorRef(mfr) === null;
              // True for `version` AND `category` alike: `deviceRef` carries both only
              // inside its `propose` arm, so either both are written or neither is
              // (Story 8.4 — the conflict note below depends on this being the same
              // predicate, or it would claim a losing value's winner "is used" on a
              // device whose attributes are not written at all).
              const willRecordVersion =
                d.match === 'unmatched' && deviceCreate[d.key] !== false && !mfrUnresolved;
              // Why it will be dropped — the two reasons differ, and telling the
              // Manager the wrong one is worse than saying nothing.
              const versionDropReason =
                d.match === 'matched' || (d.match === 'ambiguous' && devicePick[d.key])
                  ? 'device already in the catalog'
                  : 'this device is not being created';
              // Story 8.4 — the file gave two different values for ONE device. A
              // DIFFERENT fact from `willRecordVersion`/`versionDropReason`, which
              // answer "will this be written": here something WAS parsed and a
              // sibling value lost AD-24's first-non-empty tie-break. Shown whatever
              // the match state — the file contradicted itself either way, and only
              // the Manager can resolve it — and worded so it cannot read as a row
              // error, because nothing was excluded.
              //
              // It must NOT say the winner "is used" where nothing is written: beside
              // "version v5.5 not recorded — device already in the catalog", claiming
              // `v5.5 is used` contradicts the line next to it, and for `category`
              // there is no "not recorded" line at all, so the claim would stand
              // unqualified for a value that reaches no row. So the wording forks on
              // the same predicate the version rendering uses.
              const conflictNotes = (['version', 'category'] as const)
                .map((attr) => {
                  const record = d.conflicts?.[attr];
                  if (!record || record.values.length === 0) return null;
                  const won = d[attr];
                  // The loser list is capped (one vendor+model repeated across a whole
                  // export with a per-unit revision has as many losers as rows), so the
                  // overflow is named rather than dropped — the same rule every cap on
                  // the mapping step's report follows.
                  const losers =
                    record.more > 0
                      ? `${record.values.join(', ')} and ${record.more} more`
                      : record.values.join(', ');
                  return willRecordVersion
                    ? `The file also gave ${attr} ${losers} for this device; ${won} is what will be recorded (the first value in the file wins).`
                    : `The file gave more than one ${attr} for this device (${won}, ${losers}); none is recorded — ${versionDropReason}.`;
                })
                .filter((note): note is string => note !== null);
              return (
                <li key={d.key} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong>{d.model}</strong>
                  {/* Show the parsed version BEFORE committing, so a mis-mapped
                      column is visible while it can still be fixed — but plainly
                      only where it will actually be recorded. */}
                  {d.version ? (
                    willRecordVersion ? (
                      <span data-numeric>{d.version}</span>
                    ) : (
                      <span className="muted" style={{ fontSize: 13 }}>
                        version <span data-numeric>{d.version}</span> not recorded —{' '}
                        {versionDropReason}
                      </span>
                    )
                  ) : null}
                  {conflictNotes.map((note) => (
                    <span key={note} className="muted" style={{ fontSize: 13 }}>
                      {note}
                    </span>
                  ))}
                  <span className="muted">({mfr?.name ?? 'unknown vendor'})</span>
                  {d.match === 'matched' && <TextTag label="Linked" />}
                  {mfrSkipped ? (
                    <TextTag label="Auto-skipped — manufacturer not being created" />
                  ) : (
                    <>
                      {d.match === 'unmatched' && (
                        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            aria-label={`Create device ${d.model} as proposed`}
                            checked={deviceCreate[d.key] ?? true}
                            onChange={(e) => setDeviceCreate((m) => ({ ...m, [d.key]: e.target.checked }))}
                          />
                          Create as proposed
                        </label>
                      )}
                      {d.match === 'ambiguous' && (
                        <label  style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          <span className="muted">Multiple matches — pick one:</span>
                          <select
                            aria-label={`Resolve device ${d.model}`}
                            value={devicePick[d.key] ?? ''}
                            onChange={(e) => setDevicePick((m) => ({ ...m, [d.key]: e.target.value }))}
                            style={fieldStyle}
                          >
                            <option value="">Select…</option>
                            {d.candidates?.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.model} ({c.id.slice(0, 8)})
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="card stack" aria-labelledby="import-confirm-heading">
        <h2 id="import-confirm-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Confirm import
        </h2>
        {unresolved && (
          <p className="muted" style={{ margin: 0 }}>
            Resolve the ambiguous matches above before importing.
          </p>
        )}
        {/* A contradiction no longer blocks, so the Manager is TOLD it is waiting for
            them on the button rather than being stopped by it. Without this the only
            warning before the dialog is a control half a page up.
            Suppressed while `unresolved` holds the button down: promising a dialog that
            the Manager cannot reach yet points them at the wrong problem. */}
        {!unresolved && conflictSummaries.length > 0 && (
          <p className="muted" style={{ margin: 0 }}>
            The file gave more than one value for{' '}
            {conflictSummaries.length === 1 ? (
              'one site'
            ) : (
              <>
                <span data-numeric>{conflictSummaries.length}</span> sites
              </>
            )}
            .{' '}
            {conflictsWillWrite
              ? 'You will be asked to confirm what is saved.'
              : 'You will be asked to confirm that none of it is recorded.'}
          </p>
        )}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={onImportClick}
            disabled={submitting || unresolved || sites.length === 0}
          >
            {submitting ? 'Importing…' : 'Import'}
          </button>
          <button type="button" className="icon-button" onClick={reset} disabled={submitting}>
            Cancel
          </button>
        </div>
        <div role="alert" aria-live="assertive" style={{ minHeight: 16 }}>
          {/* While the dialog is open it owns the failure (ConfirmModal's `error`), and
              this region is behind the scrim and unreadable. Rendering both would put
              the same sentence on the page twice. */}
          {error && !confirmingConflicts && <TextTag label={error} tone="bad" />}
        </div>
      </section>

      {/* DEV-4444 (QA round 2) — OUTSIDE the `Confirm import` section on purpose. Its
          confirm button is Import-named, and the suite's `confirmButton()` helper
          resolves `getByRole('button', {name: /Import/})` scoped to that region; a
          second match inside it makes every unrelated import test throw on an
          ambiguous query. */}
      {confirmingConflicts && (
        <ConfirmModal
          title="Import conflicting data?"
          message={
            <div className="stack" style={{ gap: 8 }}>
              <p style={{ margin: 0 }}>
                The file gave more than one value for{' '}
                {conflictSummaries.length === 1 ? (
                  'this site'
                ) : (
                  <>
                    these <span data-numeric>{conflictSummaries.length}</span> sites
                  </>
                )}
                .{' '}
                {conflictsWillWrite
                  ? 'Importing saves one value per field and discards the rest.'
                  : 'None of these values is recorded — every one of these sites is already in your fleet.'}
              </p>
              <SiteConflictLines summaries={conflictSummaries} when="before" />
              {/* Only offered when there is a control to go back to. On a matched-only
                  conflict the review step shows no picker, so this would send the
                  Manager looking for something that is not there. "the Sites list
                  above" names the review step's own region, not the fleet screen that
                  shares the word Sites. */}
              {conflictsWillWrite && (
                <p style={{ margin: 0 }}>
                  To choose different values, cancel and change them in the Sites list above.
                </p>
              )}
            </div>
          }
          confirmLabel="Import anyway"
          busy={submitting}
          error={error}
          onConfirm={() => void onImport()}
          onCancel={() => {
            // Cancel is a dismissal, not a retry: the picks and the classification are
            // untouched, so the Manager lands back on the review step exactly as they
            // left it. The error goes, because it described an attempt they abandoned.
            //
            // REFUSED mid-commit. ConfirmModal disables both its buttons while `busy`,
            // but Esc (`useDialogA11y`) and a scrim click reach `onCancel` regardless.
            // Without this guard the dialog would vanish while the POST stayed open and
            // the wizard would then jump to the done screen — an import the Manager
            // believes they cancelled. There is no request to abort, so the honest
            // behaviour is to keep the dialog and its in-flight state on screen.
            if (submitting) return;
            setConfirmingConflicts(false);
            setError(null);
          }}
        />
      )}
    </>
  );
}
